import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs';
import { Check, Circle, LucideIconData } from 'lucide-angular';

import { AuthModalService } from '../../../../shared/services/auth-modal.service';
import { AuthService } from '../../../../shared/services/auth.service';
import {
  checkStrongPassword,
  strongPasswordValidator,
  StrongPasswordRequirement,
} from '../../../../shared/validators/strong-password.validator';
import { matchesField } from '../../../../shared/validators/matches-field.validator';
import { IconComponent } from '../../../../shared/ui/icon/icon.component';
import { InputComponent } from '../../../../shared/ui/input/input.component';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { CheckboxComponent } from '../../../../shared/ui/checkbox/checkbox.component';
import { NewsletterConsentComponent } from '../../../../shared/ui/newsletter-form/newsletter-consent.component';
import { AUTH_COPY } from '../../auth.copy';

const GENERIC_ERROR = 'Ha ocurrido un error. Por favor, inténtalo de nuevo.';
const EMAIL_TAKEN =
  'Ya existe una cuenta con ese correo. Inicia sesión o recupera tu contraseña.';
const WEAK_PASSWORD =
  'La contraseña debe tener mínimo 8 caracteres con una mayúscula, una minúscula, un número y un carácter especial.';
/**
 * Único literal que la web escribe para la rama del 409: el MENSAJE lo sirve la API
 * (spec tpv-clientes-mostrador §5.2) y aquí se pinta tal cual, sin constante propia.
 */
const RECOVER_PASSWORD_LINK = 'Recuperar contraseña';

interface RequirementRow {
  key: StrongPasswordRequirement;
  label: string;
}

const REQUIREMENTS: RequirementRow[] = [
  { key: 'length', label: 'Mínimo 8 caracteres' },
  { key: 'lowercase', label: 'Una minúscula' },
  { key: 'uppercase', label: 'Una mayúscula' },
  { key: 'digit', label: 'Un número' },
  { key: 'special', label: 'Un carácter especial' },
];

@Component({
  selector: 'app-register-form',
  imports: [
    ReactiveFormsModule,
    IconComponent,
    InputComponent,
    ButtonComponent,
    CheckboxComponent,
    NewsletterConsentComponent,
  ],
  templateUrl: './register-form.component.html',
  styleUrl: './register-form.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterFormComponent {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly authModalService = inject(AuthModalService);

  protected readonly loading = signal(false);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly errorShowsLoginLink = signal(false);
  protected readonly errorShowsRecoverLink = signal(false);
  protected readonly newsletterChecked = signal(false);

  protected readonly checkIcon: LucideIconData = Check;
  protected readonly circleIcon: LucideIconData = Circle;
  protected readonly requirements = REQUIREMENTS;
  protected readonly recoverPasswordLink = RECOVER_PASSWORD_LINK;
  // Por binding, no `id="…"` estático: un atributo estático se duplica en el host de app-checkbox y la etiqueta apunta al host.
  protected readonly newsletterId = 'auth-register-newsletter';

  protected readonly form = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(60)]],
    surnames: ['', [Validators.required, Validators.maxLength(80)]],
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, strongPasswordValidator()]],
    password_confirmation: [
      '',
      [Validators.required, matchesField('password')],
    ],
    newsletter: [false],
    type: ['cliente' as const],
  });

  private readonly passwordValue = toSignal(
    this.form.controls.password.valueChanges,
    { initialValue: this.form.controls.password.value },
  );

  protected readonly requirementStatus = computed(() =>
    checkStrongPassword(this.passwordValue() ?? ''),
  );

  protected onSubmit(): void {
    if (this.form.invalid || this.loading()) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = this.form.getRawValue();
    this.loading.set(true);
    this.errorMessage.set(null);
    this.errorShowsLoginLink.set(false);
    this.errorShowsRecoverLink.set(false);

    this.authService
      .register(payload)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: () => {
          this.authModalService.openAfterRegister(payload.email);
        },
        error: (err: HttpErrorResponse) => {
          // 409: el correo es de una cuenta creada en el mostrador (sin contraseña).
          // El copy viene del servidor y se pinta VERBATIM; «Iniciar sesión» sería un
          // callejón sin salida, así que se ofrece recuperar contraseña.
          if (err.status === 409) {
            const serverMessage = err.error?.error;
            if (typeof serverMessage === 'string' && serverMessage.trim() !== '') {
              this.errorMessage.set(serverMessage);
              this.errorShowsRecoverLink.set(true);
            } else {
              this.errorMessage.set(GENERIC_ERROR);
            }
            return;
          }

          const backend = err.error?.error ?? {};
          if (backend.email) {
            // Las claves llegan crudas porque la API no tiene traducciones `es`; si algún día se publican, revisar esta comparación.
            if (Array.isArray(backend.email) && backend.email.includes('validation.unique')) {
              this.errorMessage.set(EMAIL_TAKEN);
              this.errorShowsLoginLink.set(true);
            } else {
              this.errorMessage.set(AUTH_COPY.REGISTER.EMAIL_INVALID);
            }
          } else if (backend.password) {
            this.errorMessage.set(WEAK_PASSWORD);
            this.form.controls.password.reset('');
            this.form.controls.password_confirmation.reset('');
          } else {
            this.errorMessage.set(GENERIC_ERROR);
          }
        },
      });
  }

  protected switchToLogin(): void {
    this.authModalService.switchMode('login');
  }

  protected switchToForgotPassword(): void {
    this.authModalService.switchMode('forgot');
  }

  protected onNewsletterChange(checked: boolean): void {
    this.newsletterChecked.set(checked);
    this.form.controls.newsletter.setValue(checked);
  }
}
